import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizer } from "../contexts/OrganizerContext";
import { apiClient } from "../utils/api";
import { toast } from "react-toastify";
import { getAppUrl } from "../shared-config";
import {
	formatJapaneseDateWithWeekday,
	formatJapaneseTime,
} from "../utils/dateUtils";
import { usePageTitle } from "../hooks/usePageTitle";

// 型定義を API レスポースに合わせて更新
interface Ticket {
	id: number;
	name: string;
	price: number;
	is_free: boolean;
	has_limit: boolean;
	quantity: number | null;
	sold_tickets: number;
	description?: string;
	sale_start_date_time?: string;
	sale_end_date_time?: string;
}

interface Organizer {
	id: number;
	external_id: string;
	organizer_name: string;
	description: string;
	logo_url: string;
	website: string;
	organizer_type: string;
}

interface Event {
	id: number;
	external_id: string;
	title: string;
	slug: string;
	description: string;
	date: string;
	time: string;
	venue_name: string;
	prefecture: string;
	city: string;
	address: string;
	image_url: string;
	background_image_url?: string;
	hashtags?: string[];
	status: string;
	ticket_types: Ticket[];
	organizer?: Organizer;
	event_images?: {
		id: number;
		public_url: string;
		is_primary: boolean;
		order: number;
	}[];
	opening_times?: {
		id: number;
		open_time: string;
		start_time: string;
		end_time: string;
		description?: string;
	}[];
	cautions?: {
		id: number;
		content: string;
		position?: number;
	}[];
}

const EventDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>(); // id は slug の可能性あり
	const navigate = useNavigate();
	const { isAuthenticated } = useAuth();
	const { organizer, isOrganizer } = useOrganizer();
	const [selectedTickets, setSelectedTickets] = useState<{
		[key: number]: number;
	}>({});

	// 追加状態
	const [event, setEvent] = useState<Event | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// 表示する画像を管理するステート
	const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
	const [fadeIn, setFadeIn] = useState(true);

	usePageTitle(event?.title, { fallback: "イベント詳細" });

	// 現在のユーザーがこのイベントの主催者かどうかを判定
	const isEventOrganizer = useMemo(() => {
		if (!isAuthenticated || !isOrganizer || !organizer || !event?.organizer) {
			return false;
		}
		// 主催者が認証済みで、イベントの主催者IDと一致する場合のみtrue
		return organizer.is_verified && organizer.id === event.organizer.id;
	}, [isAuthenticated, isOrganizer, organizer, event]);

	// すべての画像を適切な順序でリスト化 - useMemo を条件分岐より前に移動
	const allImages = useMemo(() => {
		// イベントがnullの場合は空の配列を返す
		if (!event) return [];

		// プライマリ画像があれば最初に配置
		const primaryImage = event.event_images?.find((img) => img.is_primary);
		const otherImages = event.event_images
			? event.event_images
					.filter((img) => !img.is_primary)
					.sort((a, b) => a.order - b.order)
			: [];

		// すべての画像のリストを作成
		const images = [];

		// プライマリ画像があれば追加
		if (primaryImage) {
			images.push(primaryImage);
		}

		// その他の画像を追加
		images.push(...otherImages);

		// event_imagesがない場合はメイン画像を使用
		if (images.length === 0 && event.image_url) {
			images.push({
				id: 0,
				public_url: event.image_url,
				is_primary: true,
				order: 0,
			});
		}

		// 画像がない場合はプレースホルダーを使用
		if (images.length === 0) {
			images.push({
				id: 0,
				public_url: "https://via.placeholder.com/800x450",
				is_primary: true,
				order: 0,
			});
		}

		return images;
	}, [event]);

	// イベントデータの取得
	useEffect(() => {
		const fetchEventData = async () => {
			if (!id) {
				setError("イベントIDが指定されていません");
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				// イベント詳細API呼び出し（slug/ID/ULID を同一エンドポイントで扱う）
				const response = await apiClient.get(`/events/${id}`);

				// イベントデータを設定
				if (response.data && (response.data.event || response.data)) {
					const eventData = response.data.event || response.data;
					setEvent(eventData);
				} else {
					throw new Error("イベントデータが見つかりませんでした");
				}

				setIsLoading(false);
			} catch (error) {
				console.error("イベントデータの取得に失敗しました:", error);
				setError("イベントデータの取得に失敗しました");
				setIsLoading(false);
				toast.error("イベントデータの取得に失敗しました");
			}
		};

		fetchEventData();
	}, [id]);

	const handleTicketChange = (ticketId: number, quantity: number) => {
		setSelectedTickets((prev) => ({
			...prev,
			[ticketId]: quantity,
		}));
	};

	const handlePurchase = () => {
		if (!isAuthenticated) {
			// アカウントアプリのログインページにリダイレクト
			const accountUrl = getAppUrl("accountApp");
			window.location.href = `${accountUrl}/login?redirect=${encodeURIComponent(
				window.location.href
			)}`;
			return;
		}

		const ticketSelections = Object.entries(selectedTickets)
			.filter(([_, quantity]) => quantity > 0)
			.map(([ticketId, quantity]) => ({
				ticketId: parseInt(ticketId),
				quantity,
			}));

		if (ticketSelections.length === 0) {
			toast.error("チケットを選択してください");
			return;
		}

		// Stripeチェックアウトページに遷移（ULID）
		if (!event) {
			toast.error("イベント情報が取得できません");
			return;
		}
		navigate(`/checkout/${event.external_id}`, {
			state: { tickets: ticketSelections },
		});
	};

	// ローディング表示
	if (isLoading) {
		return (
			<div className="max-w-4xl mx-auto py-10 text-center">
				<p className="text-gray-600 dark:text-gray-300">
					イベント情報を読み込み中...
				</p>
			</div>
		);
	}

	// エラー表示
	if (error || !event) {
		return (
			<div className="max-w-4xl mx-auto py-10 text-center">
				<p className="text-red-600 dark:text-red-400">
					{error || "イベントが見つかりませんでした"}
				</p>
				<button
					onClick={() => navigate("/")}
					className="mt-4 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded transition-all"
				>
					トップページに戻る
				</button>
			</div>
		);
	}

	const cautionList =
		event.cautions && event.cautions.length > 0
			? [...event.cautions]
					.filter((caution) => (caution.content || "").trim().length > 0)
					.sort(
						(a, b) =>
							(a.position ?? event.cautions?.indexOf(a) ?? 0) -
							(b.position ?? event.cautions?.indexOf(b) ?? 0)
					)
			: [];

	// 利用可能なチケットをフィルタリング
	const availableTickets = event.ticket_types.filter((ticket) => {
		const now = new Date();

		// 販売開始前または販売終了後はスキップ
		if (
			ticket.sale_start_date_time &&
			new Date(ticket.sale_start_date_time) > now
		) {
			return false;
		}

		if (
			ticket.sale_end_date_time &&
			new Date(ticket.sale_end_date_time) < now
		) {
			return false;
		}

		// 制限ありで売り切れの場合はスキップ
		if (
			ticket.has_limit &&
			ticket.quantity !== null &&
			ticket.sold_tickets >= ticket.quantity
		) {
			return false;
		}

		return true;
	});

	const totalAmount = Object.entries(selectedTickets).reduce(
		(sum, [ticketId, quantity]) => {
			const ticketIdNum = parseInt(ticketId);
			const ticket = event.ticket_types.find((t) => t.id === ticketIdNum);
			if (!ticket || ticket.is_free) return sum;
			return sum + Math.round(ticket.price) * quantity;
		},
		0
	);

	// メイン画像のURL
	const mainImage = allImages[selectedImageIndex]?.public_url;
	const heroBackground = event.background_image_url || mainImage;
	const formattedDate = formatJapaneseDateWithWeekday(event.date);
	const formattedTime = formatJapaneseTime(event.time);
	const venueLabel =
		event.venue_name || `${event.prefecture} ${event.city} ${event.address}`;

	const handleHashtagClick = (tag: string) => {
		const encoded = encodeURIComponent(`#${tag}`);
		navigate(`/events?keyword=${encoded}`);
	};

	// サムネイル画像クリック時の処理
	const handleThumbnailClick = (index: number) => {
		if (index !== selectedImageIndex) {
			setFadeIn(false);
			setTimeout(() => {
				setSelectedImageIndex(index);
				setFadeIn(true);
			}, 200); // フェードアウト時間
		}
	};

	return (
		<div className="max-w-6xl mx-auto space-y-6 pb-10">
			{/* Back Button */}
			<div className="pt-6">
				<button
					onClick={() => navigate(-1)}
					className="flex items-center text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors"
				>
					<svg
						className="w-5 h-5 mr-1"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
					戻る
				</button>
			</div>

			{/* Event Header */}
			<section className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/5 text-white">
				<div className="absolute inset-0">
					{heroBackground ? (
						<img
							src={heroBackground}
							alt={`${event.title} 背景`}
							className="w-full h-full object-cover blur-sm scale-110"
						/>
					) : (
						<div className="w-full h-full bg-gradient-to-br from-slate-700 via-gray-800 to-black" />
					)}
				</div>
				<div className="absolute inset-0 bg-black/70" />
				<div className="relative flex flex-col lg:flex-row">
					<div className="flex-1 p-6 sm:p-10 space-y-6">
						<div className="space-y-2">
							<p className="text-xs uppercase tracking-[0.35em] text-white/60">
								Ticket Information
							</p>
							<div className="flex items-center justify-between gap-4">
								<h1 className="text-3xl sm:text-4xl font-bold leading-tight">
									{event.title}
								</h1>
								{isEventOrganizer && (
									<button
										onClick={() =>
											navigate(`/organizer/events/${event.external_id}/edit`)
										}
										className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-200 border border-white/20 hover:border-white/40 backdrop-blur-sm"
									>
										<svg
											className="w-4 h-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
											/>
										</svg>
										<span className="text-sm font-medium">編集</span>
									</button>
								)}
							</div>
						</div>
						<div className="space-y-4 text-base text-white/90">
							<div className="flex items-start gap-3">
								<svg
									className="w-6 h-6 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
									/>
								</svg>
								<div>
									<div className="text-lg font-semibold">{formattedDate}</div>
									{event.opening_times && event.opening_times.length > 0 ? (
										event.opening_times.length === 1 ? (
											<div className="text-white/70">
												{event.opening_times[0].start_time.replace(
													/(\d+):(\d+).*/,
													"$1:$2"
												)}
												開演
											</div>
										) : (
											<div className="text-white/70 space-y-1">
												{event.opening_times.map((time, index) => (
													<div key={index}>
														{time.description || `第${index + 1}部`}:{" "}
														{time.start_time.replace(/(\d+):(\d+).*/, "$1:$2")}
													</div>
												))}
											</div>
										)
									) : (
										formattedTime && (
											<div className="text-white/70">{formattedTime}</div>
										)
									)}
								</div>
							</div>
							<div className="flex items-start gap-3">
								<svg
									className="w-6 h-6 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
									/>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
									/>
								</svg>
								<div>
									<div className="text-lg font-semibold">{venueLabel}</div>
									<div className="text-white/70">
										{`${event.prefecture} ${event.city}`.trim()}
									</div>
								</div>
							</div>
						</div>
						<div className="flex flex-wrap gap-3 text-sm text-white/80">
							<span className="px-3 py-1 rounded-full border border-white/40">
								{event.status === "published" ? "販売中" : "準備中"}
							</span>
							{event.hashtags &&
								event.hashtags.length > 0 &&
								event.hashtags.map((tag) => (
									<span
										key={tag}
										onClick={() => handleHashtagClick(tag)}
										className="px-3 py-1 rounded-full border border-white/30 bg-white/10 text-white cursor-pointer hover:bg-white/20 transition-colors"
									>
										#{tag}
									</span>
								))}
						</div>
					</div>
					<div className="w-full lg:w-[360px] p-6 sm:p-10 pt-0 lg:pt-10">
						<div className="bg-white/5 rounded-2xl border border-white/15 shadow-2xl overflow-hidden">
							<div
								className={`aspect-[4/5] transition-opacity duration-300 ${
									fadeIn ? "opacity-100" : "opacity-0"
								}`}
							>
								<img
									src={mainImage}
									alt={`${event.title} メイン画像`}
									className="object-cover w-full h-full"
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{allImages.length > 1 && (
				<div className="flex space-x-3 overflow-x-auto pb-2">
					{allImages.map((image, index) => (
						<div
							key={image.id || index}
							className={`flex-shrink-0 cursor-pointer relative transition-all duration-200 ${
								selectedImageIndex === index
									? ""
									: "opacity-40 hover:opacity-100"
							}`}
							onClick={() => handleThumbnailClick(index)}
						>
							<img
								src={image.public_url}
								alt={`${event.title} イメージ ${index + 1}`}
								className="h-16 w-24 object-cover rounded-lg shadow-sm"
							/>
						</div>
					))}
				</div>
			)}
			{/* Organizer Section */}
			{event.organizer && (
				<section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-transparent dark:border-gray-700">
					<div className="p-6">
						<h2 className="text-xl font-bold dark:text-white mb-4">主催者</h2>
						<div
							onClick={() =>
								navigate(`/organizers/${event.organizer?.external_id}`)
							}
							className="flex items-center gap-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-4 rounded-xl transition-all"
						>
							{event.organizer.logo_url && (
								<div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-blue-500/20">
									<img
										src={event.organizer.logo_url}
										alt={event.organizer.organizer_name}
										className="w-full h-full object-cover"
									/>
								</div>
							)}
							<div className="flex-1">
								<h3 className="font-semibold text-lg dark:text-white">
									{event.organizer.organizer_name}
								</h3>
								{event.organizer.description && (
									<p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
										{event.organizer.description}
									</p>
								)}
							</div>
							<svg
								className="w-6 h-6 text-gray-400 dark:text-gray-500"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9 5l7 7-7 7"
								/>
							</svg>
						</div>
					</div>
				</section>
			)}

			{/* Event Details - Improved Card Design */}
			<section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-transparent dark:border-gray-700">
				<div className="px-6 pt-6 pb-2">
					<h2 className="text-xl font-bold dark:text-white">イベント詳細</h2>
				</div>

				{/* Event Description */}
				<div className="p-6">
					<p className="whitespace-pre-line text-gray-700 dark:text-gray-300 leading-relaxed">
						{event.description}
					</p>
				</div>

				{/* Key Event Info Cards */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 pt-0">
					{/* Date & Time Card */}
					<div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 flex items-start space-x-3">
						<div className="bg-blue-100 dark:bg-blue-800/40 rounded-full p-3 text-blue-600 dark:text-blue-400">
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
						</div>
						<div>
							<h3 className="font-semibold text-gray-800 dark:text-gray-200">
								日時
							</h3>
							<p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
								{event.date.replace(
									/(\d+)-0?(\d+)-0?(\d+)T?.*Z?/,
									"$1年$2月$3日"
								)}
							</p>
							<p className="text-gray-600 dark:text-gray-400 text-sm">
								{event.time.replace(/(\d+):(\d+).*/, "$1:$2")}
							</p>
						</div>
					</div>

					{/* Location Card */}
					<div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 flex items-start space-x-3">
						<div className="bg-blue-100 dark:bg-blue-800/40 rounded-full p-3 text-blue-600 dark:text-blue-400">
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
								/>
							</svg>
						</div>
						<div>
							<h3 className="font-semibold text-gray-800 dark:text-gray-200">
								会場
							</h3>
							<p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
								{event.venue_name || ""}
							</p>
							<p className="text-gray-600 dark:text-gray-400 text-sm">{`${event.prefecture} ${event.city} ${event.address}`}</p>
						</div>
					</div>

					{/* Schedule Card */}
					<div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 flex items-start space-x-3">
						<div className="bg-blue-100 dark:bg-blue-800/40 rounded-full p-3 text-blue-600 dark:text-blue-400">
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
						<div>
							<h3 className="font-semibold text-gray-800 dark:text-gray-200">
								スケジュール
							</h3>
							{!event.opening_times || event.opening_times.length === 0 ? (
								<p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
									詳細はイベント主催者にお問い合わせください
								</p>
							) : event.opening_times.length === 1 ? (
								// 単一のスケジュールの場合
								<div className="text-gray-600 dark:text-gray-400 text-sm mt-1">
									<p>
										開場:{" "}
										{event.opening_times[0].open_time.replace(
											/(\d+):(\d+).*/,
											"$1:$2"
										)}
									</p>
									<p>
										開演:{" "}
										{event.opening_times[0].start_time.replace(
											/(\d+):(\d+).*/,
											"$1:$2"
										)}
									</p>
									<p>
										終演:{" "}
										{event.opening_times[0].end_time.replace(
											/(\d+):(\d+).*/,
											"$1:$2"
										)}
									</p>
								</div>
							) : (
								// 複数のスケジュールの場合は description (第一部、第二部など) を使用
								<div className="text-gray-600 dark:text-gray-400 text-sm mt-1">
									{event.opening_times.map((time, index) => (
										<p key={index}>
											{time.description || `第${index + 1}部`}:{" "}
											{time.start_time.replace(/(\d+):(\d+).*/, "$1:$2")}~
										</p>
									))}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Map Container */}
				{(event.prefecture || event.city || event.address) && (
					<div className="p-6 pt-0">
						<div className="bg-gray-100 dark:bg-gray-700 rounded-xl h-48 overflow-hidden relative">
							<div className="absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-500">
								<svg
									className="w-8 h-8"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
									/>
								</svg>
								<span className="ml-2">会場の地図</span>
							</div>
							{/* You would integrate an actual map here */}
						</div>
					</div>
				)}

				{/* Detailed Schedule - Only show when there are 2 or more items */}
				{event.opening_times && event.opening_times.length >= 2 && (
					<div className="p-6 pt-0">
						<h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">
							タイムスケジュール
						</h3>
						<div className="space-y-3">
							{event.opening_times.map((time, index) => (
								<div
									key={index}
									className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl border border-gray-100 dark:border-gray-600"
								>
									<div className="flex flex-wrap justify-between">
										<p className="text-gray-800 dark:text-gray-200 font-medium">
											{time.description || `第${index + 1}部`}
										</p>
									</div>
									<div className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex space-x-4">
										<span>
											開場: {time.open_time.replace(/(\d+):(\d+).*/, "$1:$2")}
										</span>
										<span>
											開演: {time.start_time.replace(/(\d+):(\d+).*/, "$1:$2")}
										</span>
										<span>
											終演: {time.end_time.replace(/(\d+):(\d+).*/, "$1:$2")}
										</span>
									</div>
								</div>
							))}
						</div>
					</div>
				)}
			</section>

			{cautionList.length > 0 && (
				<section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-transparent dark:border-gray-700">
					<div className="p-6">
						<h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
							注意事項
						</h3>
						<ul className="space-y-3 text-gray-700 dark:text-gray-300">
							{cautionList.map((caution) => (
								<li key={caution.id} className="flex items-start gap-2">
									<span className="text-blue-500 dark:text-blue-300 mt-1">
										・
									</span>
									<span>{caution.content}</span>
								</li>
							))}
						</ul>
					</div>
				</section>
			)}

			{/* チケット選択 - モダンUI */}
			<section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-transparent dark:border-gray-700">
				<div className="p-6 border-b dark:border-gray-700">
					<h2 className="text-xl font-bold dark:text-white">チケット選択</h2>
				</div>

				{availableTickets.length === 0 ? (
					<div className="text-center py-10 text-gray-500 dark:text-gray-400">
						<svg
							className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
							/>
						</svg>
						<p>現在購入可能なチケットはありません</p>
					</div>
				) : (
					<>
						{/* チケット価格オプション */}
						<div className="px-6 py-6">
							<h3 className="text-xl font-bold dark:text-white mb-6 mt-2">
								料金
							</h3>
							<div className="space-y-3">
								{availableTickets.map((ticket) => {
									// 残りチケット数の計算
									const remaining =
										ticket.has_limit && ticket.quantity !== null
											? Math.max(0, ticket.quantity - ticket.sold_tickets)
											: 99;

									const isSelected = selectedTickets[ticket.id] > 0;

									return (
										<div
											key={ticket.id}
											className={`rounded-xl border p-4 flex justify-between items-center cursor-pointer transition-all ${
												isSelected
													? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20"
													: "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
											}`}
											onClick={() =>
												handleTicketChange(ticket.id, isSelected ? 0 : 1)
											}
										>
											<div className="flex items-center">
												<div
													className={`rounded-full p-2 mr-3 ${
														isSelected
															? "bg-blue-100 dark:bg-blue-800/40 text-blue-600 dark:text-blue-400"
															: "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
													}`}
												>
													{ticket.is_free ? (
														<svg
															className="w-5 h-5"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
															xmlns="http://www.w3.org/2000/svg"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
															/>
														</svg>
													) : (
														<svg
															className="w-5 h-5"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
															xmlns="http://www.w3.org/2000/svg"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
															/>
														</svg>
													)}
												</div>
												<div>
													<h4 className="font-semibold dark:text-white">
														{ticket.name}
													</h4>
													{ticket.description && (
														<p className="text-sm text-gray-500 dark:text-gray-400">
															{ticket.description}
														</p>
													)}
													<div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
														{ticket.has_limit
															? `残り${remaining}枚`
															: "制限なし"}
													</div>
												</div>
											</div>
											<div className="text-right">
												<div className="text-lg font-bold dark:text-white">
													{ticket.is_free
														? "無料"
														: `¥${Math.round(ticket.price).toLocaleString()}`}
												</div>
												<div className="mt-1">
													<select
														value={selectedTickets[ticket.id] || 0}
														onChange={(e) =>
															handleTicketChange(
																ticket.id,
																parseInt(e.target.value)
															)
														}
														className="text-sm px-2 py-1 border dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white"
														onClick={(e) => e.stopPropagation()}
													>
														{[...Array(Math.min(4, remaining + 1))].map(
															(_, i) => (
																<option key={i} value={i}>
																	{i}枚
																</option>
															)
														)}
													</select>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</div>

						{/* Purchase Button - Fixed Bottom */}
						<div className="sticky bottom-0 p-6 bg-white dark:bg-gray-800 border-t dark:border-gray-700">
							<div className="flex justify-between items-center mb-4">
								<span className="text-gray-600 dark:text-gray-400">合計</span>
								<span className="text-2xl font-bold dark:text-white">
									¥{totalAmount.toLocaleString()}
								</span>
							</div>
							<button
								onClick={handlePurchase}
								className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 rounded-xl font-bold transition-all"
							>
								{isAuthenticated
									? "チケットを購入する"
									: "ログインしてチケットを購入"}
							</button>
							<p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
								チケット購入後のキャンセルはできません
							</p>
						</div>
					</>
				)}
			</section>
		</div>
	);
};

export default EventDetailPage;
